/**
 * /admin/member-context/:email · concierge-only inspector.
 *
 * Answers the question: "why did this member just open /care?"
 * Shows their themes (Sonnet), last 30 days of signals, quiet hours,
 * consent state, and their bands shown/dismissed ratio.
 *
 * By design this page:
 *   · NEVER shows Dreamweaver dreams or themes (silo enforced by API).
 *   · Has a "regenerate themes" button so the desk can re-run Sonnet
 *     on demand for a freshly-active member.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || "";

const T = {
  bg: "#fbf9f4", ink: "#1a1a1a", brass: "#a97e2d", brassSoft: "#c9a663",
  muted: "rgba(26,26,26,0.55)", border: "rgba(26,26,26,0.10)",
  mist: "#f7f4ee",
};

function authHeaders() {
  const t = localStorage.getItem("admin_token")
        || localStorage.getItem("concierge_admin_token")
        || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function AdminMemberContext() {
  const { email } = useParams();
  const [data,  setData]  = useState(null);
  const [error, setError] = useState("");
  const [busy,  setBusy]  = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [email]);

  async function load() {
    setError("");
    try {
      const r = await axios.get(
        `${API}/api/admin/member-context/${encodeURIComponent(email)}`,
        { headers: authHeaders() }
      );
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }

  async function regen() {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/admin/member-context/${encodeURIComponent(email)}/regenerate-themes`,
        {},
        { headers: authHeaders() }
      );
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div data-testid="admin-member-context"
      style={{
        minHeight: "100vh", background: T.bg, color: T.ink,
        fontFamily: '"EB Garamond", Georgia, serif', padding: "40px 24px",
      }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div style={{ fontSize: 11, letterSpacing: "0.24em",
                      textTransform: "uppercase", color: T.brass, marginBottom: 8 }}>
          Continuity Spine · concierge inspector
        </div>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 400 }}>
          {email}
        </h1>

        {error && (
          <div data-testid="mc-error"
            style={{ marginTop: 12, padding: "8px 12px", borderRadius: 6,
                     background: "#fdecea", color: "#8a2c1f", fontSize: 13 }}>
            {error}
          </div>
        )}

        {data && (
          <>
            {/* Context summary */}
            <section style={{ marginTop: 22, padding: 18, background: "white",
                              border: `1px solid ${T.border}`, borderRadius: 10 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)",
                            gap: 14, fontSize: 14 }}>
                <div>
                  <div style={{ fontSize: 11, letterSpacing: "0.18em",
                                textTransform: "uppercase", color: T.muted }}>
                    Cross-pillar consent
                  </div>
                  <div style={{ marginTop: 4, fontSize: 15 }}
                       data-testid="mc-consent">
                    {data.context.cross_pillar_consent ? "On" : "Off"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, letterSpacing: "0.18em",
                                textTransform: "uppercase", color: T.muted }}>
                    Quiet hours
                  </div>
                  <div style={{ marginTop: 4, fontSize: 15 }}
                       data-testid="mc-quiet">
                    {data.context.quiet_hours || "20:00-08:00 IST"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, letterSpacing: "0.18em",
                                textTransform: "uppercase", color: T.muted }}>
                    Chattiness
                  </div>
                  <div style={{ marginTop: 4, fontSize: 15 }}
                       data-testid="mc-chatt">
                    {data.context.chattiness || "chatty"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, letterSpacing: "0.18em",
                                textTransform: "uppercase", color: T.muted }}>
                    Bands · shown / dismissed
                  </div>
                  <div style={{ marginTop: 4, fontSize: 15 }}>
                    {data.context.bands_shown || 0}
                    {"  /  "}
                    {data.context.bands_dismissed || 0}
                  </div>
                </div>
              </div>
            </section>

            {/* Themes */}
            <section style={{ marginTop: 18, padding: 18, background: "#fff7e7",
                              border: `1px solid ${T.brassSoft}`, borderRadius: 10 }}>
              <div style={{ display: "flex", alignItems: "baseline",
                            justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.2em",
                              textTransform: "uppercase", color: T.brass }}>
                  Themes we noticed (Sonnet)
                </div>
                <button onClick={regen} disabled={busy}
                  data-testid="mc-regen-themes"
                  style={{
                    padding: "6px 12px", borderRadius: 6, background: "white",
                    border: `1px solid ${T.brass}`, color: T.brass,
                    fontFamily: "inherit", fontSize: 12,
                    cursor: busy ? "wait" : "pointer",
                  }}>
                  {busy ? "Weaving…" : "Regenerate"}
                </button>
              </div>
              {(!data.context.themes || data.context.themes.length === 0) ? (
                <div style={{ fontSize: 13, color: T.muted, fontStyle: "italic" }}>
                  No themes yet · click Regenerate to shape a few.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 6 }}>
                  {data.context.themes.map((t, i) => (
                    <div key={i} data-testid={`mc-theme-${i}`}
                      style={{ padding: "8px 12px", background: "white",
                               borderRadius: 6, border: `1px solid ${T.border}` }}>
                      <div style={{ fontSize: 14, color: T.ink }}>{t.label}</div>
                      {t.evidence && (
                        <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>
                          from {t.evidence}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Recent signals */}
            <section style={{ marginTop: 18, padding: 18, background: "white",
                              border: `1px solid ${T.border}`, borderRadius: 10 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.2em",
                            textTransform: "uppercase", color: T.muted, marginBottom: 10 }}>
                Recent signals · last 30 days
              </div>
              {(!data.recent_signals || data.recent_signals.length === 0) ? (
                <div style={{ fontSize: 13, color: T.muted, fontStyle: "italic" }}>
                  No signals yet.
                </div>
              ) : (
                <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: T.muted, fontSize: 11,
                                 letterSpacing: "0.1em" }}>
                      <th style={{ padding: "6px 8px" }}>When</th>
                      <th style={{ padding: "6px 8px" }}>Pillar</th>
                      <th style={{ padding: "6px 8px" }}>City</th>
                      <th style={{ padding: "6px 8px" }}>Topic</th>
                      <th style={{ padding: "6px 8px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_signals.map((s, i) => (
                      <tr key={s.id || i} data-testid={`mc-signal-${i}`}
                        style={{ borderTop: `1px solid ${T.border}` }}>
                        <td style={{ padding: "6px 8px", fontSize: 12, color: T.muted }}>
                          {s.ts ? new Date(s.ts).toLocaleString() : "—"}
                        </td>
                        <td style={{ padding: "6px 8px" }}>/{s.pillar}</td>
                        <td style={{ padding: "6px 8px" }}>{s.city || "—"}</td>
                        <td style={{ padding: "6px 8px" }}>{s.subject || "—"}</td>
                        <td style={{ padding: "6px 8px", fontSize: 12, color: T.muted }}>
                          {s.action || "request"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div style={{ marginTop: 14, fontSize: 11, color: T.muted,
                            fontStyle: "italic" }}>
                ⚠ Dreamweaver dreams NOT shown here by design · §L1 silo
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
